'use client';

import { useState, useEffect } from 'react';
import ProjectHero from './ProjectHero';
import ProjectFilters from './ProjectFilter';
import ProjectGrid from './ProjectGrid';
import ProjectModal from './ProjectModal';

import type { Project } from '@/type/project';

const ProjectsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const projects: Project[] = [
    {
      id: 1,
      title: "Credit Card Fraud Detection",
      category: "Machine Learning",
      description:
        "Built an anomaly detection system using classification models to identify fraudulent credit card transactions with high precision and recall, minimizing financial losses.",
      tags: ["Python", "Scikit-learn", "XGBoost", "EDA"],
      technologies: ["Python", "Pandas", "Scikit-learn", "XGBoost"],
      githubUrl: "https://github.com/Shreyabhat11/Credit-card-fraud-detection",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d",
      alt: 'Climate scientist analyzing weather data on computer with multiple screens showing temperature maps and precipitation charts',
      status: 'completed'
    },

    {
      id: 2,
      title: "Netflix Data Analysis",
      category: "Data Analytics",
      description:
        "Performed exploratory data analysis on Netflix titles to uncover trends in genres, content types, release patterns, and viewer preferences using interactive visualizations.",
      tags: ["Python", "EDA", "Visualization"],
      technologies: ["Python", "Pandas", "Matplotlib", "Seaborn"],
      githubUrl: "https://github.com/Shreyabhat11/netflix-data-analysis",
      image: "https://images.unsplash.com/photo-1586899028174-e7098604235b",
      alt: 'Climate scientist analyzing weather data on computer with multiple screens showing temperature maps and precipitation charts',
      status: 'completed'
    },

    {
      id: 3,
      title: "Telecom Churn Data Analysis",
      category: "Analytics",
      description:
        "Analyzed customer behavior and service usage data to identify churn drivers and business insights that improve retention strategies.",
      tags: ["SQL", "Python", "EDA"],
      technologies: ["Python", "SQL", "Power BI"],
      githubUrl: "https://github.com/Shreyabhat11/Telecom-customer-churn-EDA",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
      alt: 'Climate scientist analyzing weather data on computer with multiple screens showing temperature maps and precipitation charts',
      status: 'completed'
    },

    {
      id: 4,
      title: "Movie Recommendation System",
      category: "Machine Learning",
      description:
        "Implemented collaborative filtering and content-based recommendation models to suggest personalized movies based on user preferences.",
      tags: ["Recommendation", "ML"],
      technologies: ["Python", "Scikit-learn", "Cosine Similarity"],
      githubUrl: "https://github.com/Shreyabhat11/Movie_recommendation",
      image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba",
      alt: "Loading",
      status: 'completed'

    },

    {
      id: 5,
      title: "Resume Skill Matcher",
      category: "NLP",
      description:
        "Developed an NLP system that matches resumes to job descriptions by extracting skills and computing similarity scores to automate candidate screening.",
      tags: ["NLP", "Text Processing"],
      technologies: ["Python", "TF-IDF", "Cosine Similarity", "Flask"],
      githubUrl: "https://github.com/Shreyabhat11/resume-matcher",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40",
      alt: "Loading",
      status: 'completed'
    },

    {
      id: 6,
      title: "Healthify Clone",
      category: "Full Stack",
      description:
        "Built a health tracking web app to monitor diet, calories, and workouts with personalized insights and progress visualization.",
      tags: ["React", "Full Stack"],
      technologies: ["React", "Node.js", "MongoDB"],
      githubUrl: "https://github.com/Shreyabhat11/Healthify_clone",
      image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438",
      alt: "Loading",
      status: 'completed'
    },

    {
      id: 7,
      title: "Structural Defect Analyser",
      category: "Computer Vision",
      description:
        "Used computer vision techniques to detect cracks and structural defects from images for automated infrastructure inspection.",
      tags: ["CV", "Deep Learning"],
      technologies: ["TensorFlow", "OpenCV", "CNN"],
      githubUrl: "https://github.com/Shreyabhat11/structural_defect",
      image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e",
      alt: "Loading",
      status: 'completed'
    },

    {
      id: 8,
      title: "RAG Chatbot",
      category: "Generative AI",
      description:
        "Built a Retrieval-Augmented Generation chatbot that answers questions using custom documents with vector search and LLMs.",
      tags: ["LLM", "RAG"],
      technologies: ["LangChain", "FAISS", "OpenAI API", "Python"],
      githubUrl: "https://github.com/Shreyabhat11/RAG_chatbot",
      liveUrl: "https://rag-chatbot-demo.vercel.app",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995",
      alt: "Loading",
      status: 'completed'
    },

    {
      id: 9,
      title: "Generative AI Test Data Synthesis",
      category: "Generative AI",
      description:
        "Generated realistic synthetic datasets using generative models to preserve privacy while maintaining statistical similarity to real-world data.",
      tags: ["GAN", "Synthetic Data"],
      technologies: ["Python", "GANs", "TensorFlow"],
      githubUrl: "https://github.com/Shreyabhat11/Synthetic_data_generation",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
      alt: "Loading",
      status: 'completed'
    },

    {
      id: 10,
      title: "AI Hallucination Detector",
      category: "LLM Evaluation",
      description:
        "Designed a system to detect hallucinations in LLM outputs by validating responses against factual references and confidence scoring.",
      tags: ["LLM", "Evaluation"],
      technologies: ["Python", "LLMs", "Streamlit"],
      githubUrl: "https://github.com/Shreyabhat11/ai-hallucination-detector",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995",
      alt: "Loading",
      status: 'completed'
    },

    {
      id: 11,
      title: "AI Symptom Checker",
      category: "Healthcare AI",
      description:
        "Built an AI-powered medical assistant that predicts possible diseases from symptoms and suggests preventive measures, diet, and workouts.",
      tags: ["Healthcare", "ML"],
      technologies: ["Django", "ML Models", "Python"],
      githubUrl: "https://github.com/Shreyabhat11/ai_symptom_checker",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef",
      alt: "Loading",
      status: 'completed'
    },
    {
      id: 12,
      title: "Jarvis Agent",
      category: "AI Assistant / Automation",
      description:
        "Developed a JARVIS-like AI assistant that integrates LLMs, voice/text commands, and task automation. It can answer queries, summarize content, execute workflows, and assist daily productivity across desktop and mobile devices.",
      tags: ["AI", "Automation", "LLM"],
      technologies: [
        "Python",
        "Flask",
        "React",
        "Gemini API",
        "Speech Recognition",
        "REST APIs"
      ],
      githubUrl: "https://github.com/Shreyabhat11/jarvis-agent",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995",
      alt: "AI assistant dashboard interface",
      status: "completed"
    },
    {
      id: 13,
      title: "AI MOM Generator",
      category: "Productivity AI",
      description:
        "Built an AI-powered Meeting Minutes (MOM) generator that converts meeting audio or notes into structured summaries, action items, and decisions using NLP and LLMs. Helps teams save time and automate documentation.",
      tags: ["NLP", "Productivity", "AI"],
      technologies: [
        "Python",
        "Streamlit",
        "Gemini API",
        "Speech-to-Text",
        "Text Summarization",
        "NLP"
      ],
      githubUrl: "https://github.com/Shreyabhat11/MOM_generator",
      image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df",
      alt: "AI meeting notes summarization app",
      status: "completed"
    },
    {
      id: 14,
      title: "AskQL v1 — Rule-Based NL to SQL",
      category: "AI / NLP",
      description:
        "Built a rule-based Natural Language to SQL system that converts simple text queries into SQL using regex and keyword matching. This version focuses on deterministic logic and demonstrates foundational NLP-to-database interaction.",
      tags: ["Rule-Based NLP", "SQL", "Backend"],
      technologies: [
        "Python",
        "FastAPI",
        "SQLite",
        "Regex",
        "Rule-Based Parsing"
      ],
      githubUrl: "https://github.com/Shreyabhat11/askql-level1",
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475",
      alt: "Rule-based NL to SQL system architecture",
      status: "completed"
    },
    {
      id: 15,
      title: "AskQL v2 — NLP + Machine Learning",
      category: "AI / Machine Learning",
      description:
        "Enhanced NL to SQL system using classical NLP and Machine Learning. Implemented TF-IDF and Logistic Regression for intent classification along with entity extraction to improve flexibility and handle varied user queries.",
      tags: ["Machine Learning", "NLP", "SQL"],
      technologies: [
        "Python",
        "FastAPI",
        "scikit-learn",
        "TF-IDF",
        "Logistic Regression",
        "SQLite"
      ],
      githubUrl: "https://github.com/Shreyabhat11/askql-level2",
      image: "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb",
      alt: "ML-based NL to SQL pipeline",
      status: "completed"
    },
    {
      id: 16,
      title: "AskQL v3 — Hybrid ML + LLM NL to SQL",
      category: "Generative AI",
      description:
        "Developed a production-style hybrid NL to SQL system combining ML-based intent detection with LLM-driven SQL generation. Added structured prompting, validation layer, and error correction loop to handle complex queries, joins, and real-world scenarios.",
      tags: ["LLM", "GenAI", "Hybrid AI", "NLP"],
      technologies: [
        "Python",
        "FastAPI",
        "scikit-learn",
        "LLM Integration",
        "Prompt Engineering",
        "SQLite",
        "React"
      ],
      githubUrl: "https://github.com/Shreyabhat11/askql-level1",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995",
      alt: "Hybrid ML and LLM architecture for NL to SQL",
      status: "completed"
    }


  ];
  const categories = ['All', 'Machine Learning', 'NLP', 'IoT Analytics', 'Data Engineering', 'Operations Research', 'Data Science'];

  const allTags = Array.from(new Set(projects.flatMap((p) => p.tags))).sort();

  const filteredProjects = projects.filter((project) => {
    const categoryMatch = selectedCategory === 'All' || project.category === selectedCategory;
    const tagMatch = selectedTags.length === 0 || selectedTags.some((tag) => project.tags.includes(tag));
    const searchMatch = searchQuery === '' ||
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    return categoryMatch && tagMatch && searchMatch;
  });

  const handleProjectClick = (project: Project) => {
    if (!isHydrated) return;
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isHydrated) return;
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  const handleCategoryChange = (category: string) => {
    if (!isHydrated) return;
    setSelectedCategory(category);
  };

  const handleTagToggle = (tag: string) => {
    if (!isHydrated) return;
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSearchChange = (query: string) => {
    if (!isHydrated) return;
    setSearchQuery(query);
  };

  const handleClearFilters = () => {
    if (!isHydrated) return;
    setSelectedCategory('All');
    setSelectedTags([]);
    setSearchQuery('');
  };

  if (!isHydrated) {
    return (
      <main data-component-id="src/app/projects/components/ProjectsInteractive.tsx:335:6" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="335" data-component-file="ProjectsInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-16%22%7D" className="min-h-screen bg-background pt-16">
        <div data-component-id="src/app/projects/components/ProjectsInteractive.tsx:336:8" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="336" data-component-file="ProjectsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div data-component-id="src/app/projects/components/ProjectsInteractive.tsx:337:10" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="337" data-component-file="ProjectsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/projects/components/ProjectsInteractive.tsx:338:12" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="338" data-component-file="ProjectsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-lg%22%7D" className="h-64 bg-muted rounded-lg"></div>
            <div data-component-id="src/app/projects/components/ProjectsInteractive.tsx:339:12" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="339" data-component-file="ProjectsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-16%20bg-muted%20rounded-lg%22%7D" className="h-16 bg-muted rounded-lg"></div>
            <div data-component-id="src/app/projects/components/ProjectsInteractive.tsx:340:12" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="340" data-component-file="ProjectsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
                <div data-component-id="src/app/projects/components/ProjectsInteractive.tsx:342:16" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="342" data-component-file="ProjectsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-lg%22%7D" key={i} className="h-96 bg-muted rounded-lg"></div>
              )}
            </div>
          </div>
        </div>
      </main>);

  }

  return (
    <main data-component-id="src/app/projects/components/ProjectsInteractive.tsx:352:4" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="352" data-component-file="ProjectsInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-16%22%7D" className="min-h-screen bg-background pt-16">
      <ProjectHero data-component-id="src/app/projects/components/ProjectsInteractive.tsx:353:6" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="353" data-component-file="ProjectsInteractive.tsx" data-component-name="ProjectHero" data-component-content="%7B%22elementName%22%3A%22ProjectHero%22%7D" totalProjects={projects.length} featuredCount={projects.filter((p) => p.featured).length} />

      <div data-component-id="src/app/projects/components/ProjectsInteractive.tsx:355:6" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="355" data-component-file="ProjectsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-12%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <ProjectFilters data-component-id="src/app/projects/components/ProjectsInteractive.tsx:356:8" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="356" data-component-file="ProjectsInteractive.tsx" data-component-name="ProjectFilters" data-component-content="%7B%22elementName%22%3A%22ProjectFilters%22%7D"
          categories={categories}
          selectedCategory={selectedCategory}
          allTags={allTags}
          selectedTags={selectedTags}
          searchQuery={searchQuery}
          onCategoryChange={handleCategoryChange}
          onTagToggle={handleTagToggle}
          onSearchChange={handleSearchChange}
          onClearFilters={handleClearFilters}
          resultCount={filteredProjects.length} />


        <ProjectGrid data-component-id="src/app/projects/components/ProjectsInteractive.tsx:369:8" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="369" data-component-file="ProjectsInteractive.tsx" data-component-name="ProjectGrid" data-component-content="%7B%22elementName%22%3A%22ProjectGrid%22%7D"
          projects={filteredProjects}
          onProjectClick={handleProjectClick} />


        {selectedProject &&
          <ProjectModal data-component-id="src/app/projects/components/ProjectsInteractive.tsx:375:10" data-component-path="src/app/projects/components/ProjectsInteractive.tsx" data-component-line="375" data-component-file="ProjectsInteractive.tsx" data-component-name="ProjectModal" data-component-content="%7B%22elementName%22%3A%22ProjectModal%22%7D"
            project={selectedProject}
            isOpen={isModalOpen}
            onClose={handleCloseModal} />

        }
      </div>
    </main>);

};

export default ProjectsInteractive;